import React from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';

/**
 * Los controles sueltos de la pantalla del policía.
 *
 * Separados del panel para que éste se lea como lo que es —la lista de reglas—
 * y no como trescientas líneas de `<input>`. Cada uno lleva su etiqueta
 * accesible: la pantalla tiene varios desplegables de clientes y varios grupos
 * de casillas, y sin nombre no se distinguen ni con un lector ni desde un test.
 */

export const Bloque = ({ titulo, descripcion, children }) => (
  <div className='px-6 py-5 border-b border-slate-200 last:border-b-0'>
    <h4 className='text-sm font-medium text-slate-900'>{titulo}</h4>
    {descripcion && <p className='text-xs text-slate-500 mt-1 mb-3'>{descripcion}</p>}
    {children}
  </div>
);

/** Un interruptor con su explicación debajo. */
export const Interruptor = ({ etiqueta, detalle, aviso, checked, onChange }) => (
  <label className='flex items-start gap-2.5 text-sm text-slate-700 py-1.5'>
    <input
      type='checkbox'
      checked={Boolean(checked)}
      onChange={(e) => onChange(e.target.checked)}
      className='mt-0.5 rounded-lg border-slate-300'
      aria-label={etiqueta}
    />
    <span>
      {etiqueta}
      {detalle && <span className='block text-xs text-slate-500'>{detalle}</span>}
      {aviso && !checked && (
        <span className='block text-xs text-amber-700 mt-0.5'>{aviso}</span>
      )}
    </span>
  </label>
);

/** Un número con unidad y su porqué. */
export const Numero = ({ etiqueta, detalle, valor, onChange, min, max, step = 1, unidad }) => (
  <label className='text-sm text-slate-700'>
    <span className='block text-xs text-slate-500 mb-1'>{etiqueta}</span>
    <span className='flex items-baseline gap-1.5'>
      <input
        type='number'
        aria-label={etiqueta}
        min={min}
        max={max}
        step={step}
        value={valor ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        className='w-24 rounded-lg border-slate-300 text-sm tabular-nums'
      />
      {unidad && <span className='text-xs text-slate-400'>{unidad}</span>}
    </span>
    {detalle && <span className='block text-xs text-slate-400 mt-1 max-w-xs'>{detalle}</span>}
  </label>
);

/**
 * Un grupo de casillas sobre una lista de opciones.
 *
 * Se usa para estados, días, países y supuestos. Todas comparten forma: una
 * lista de ids donde estar dentro significa «sí».
 */
export const Casillas = ({ nombre, opciones, seleccion = [], onChange, compacto = false }) => {
  const puestos = (seleccion || []).map(String);
  const alternar = (id) => {
    const s = String(id);
    onChange(puestos.includes(s) ? puestos.filter((x) => x !== s) : [...puestos, s]);
  };
  return (
    <div className={compacto ? 'flex flex-wrap gap-1.5' : 'flex flex-wrap gap-x-5 gap-y-1.5'}>
      {opciones.map((o) => (
        <label
          key={o.id}
          className={
            compacto
              ? `cursor-pointer rounded-lg border px-2.5 py-1 text-xs ${
                  puestos.includes(String(o.id))
                    ? 'border-indigo-400 bg-indigo-50 text-indigo-800 font-medium'
                    : 'border-slate-200 text-slate-500'
                }`
              : 'flex items-center gap-1.5 text-sm text-slate-700'
          }
        >
          <input
            type='checkbox'
            aria-label={`${nombre}: ${o.nombre}`}
            checked={puestos.includes(String(o.id))}
            onChange={() => alternar(o.id)}
            className={compacto ? 'sr-only' : 'rounded-lg border-slate-300'}
          />
          {o.nombre}
        </label>
      ))}
    </div>
  );
};

/** Una lista ordenable a botonazos. Sin arrastrar: aquí no hace falta. */
export const ListaOrdenada = ({ nombre, elementos, nombreDe, onChange, vacio }) => {
  if (!elementos.length) return <p className='text-xs text-slate-400'>{vacio}</p>;
  const mover = (i, a) => {
    if (a < 0 || a >= elementos.length) return;
    const copia = [...elementos];
    [copia[i], copia[a]] = [copia[a], copia[i]];
    onChange(copia);
  };
  return (
    <ol className='space-y-1'>
      {elementos.map((id, i) => (
        <li key={id} className='flex items-center gap-2 text-sm text-slate-700'>
          <span className='w-5 text-xs text-slate-400 tabular-nums'>{i + 1}.</span>
          <span className='flex-1'>{nombreDe(id)}</span>
          <button
            type='button'
            onClick={() => mover(i, i - 1)}
            disabled={i === 0}
            aria-label={`Subir ${nombreDe(id)} en ${nombre}`}
            className='text-xs text-slate-400 hover:text-slate-800 disabled:opacity-30'
          >
            ↑
          </button>
          <button
            type='button'
            onClick={() => mover(i, i + 1)}
            disabled={i === elementos.length - 1}
            aria-label={`Bajar ${nombreDe(id)} en ${nombre}`}
            className='text-xs text-slate-400 hover:text-slate-800 disabled:opacity-30'
          >
            ↓
          </button>
          <button
            type='button'
            onClick={() => onChange(elementos.filter((x) => x !== id))}
            aria-label={`Quitar ${nombreDe(id)} de ${nombre}`}
            className='text-slate-400 hover:text-red-600'
          >
            <TrashIcon className='w-4 h-4' />
          </button>
        </li>
      ))}
    </ol>
  );
};

/** El desplegable para añadir a una lista, sin repetir lo que ya está. */
export const Anadir = ({ etiqueta, opciones, yaPuestos = [], onAdd, placeholder }) => (
  <select
    aria-label={etiqueta}
    value=''
    onChange={(e) => {
      if (!e.target.value) return;
      onAdd(e.target.value);
    }}
    className='mt-2 rounded-lg border-slate-300 text-sm'
  >
    <option value=''>{placeholder}</option>
    {opciones
      .filter((o) => !yaPuestos.map(String).includes(String(o.id)))
      .map((o) => (
        <option key={o.id} value={o.id}>
          {o.nombre}
        </option>
      ))}
  </select>
);
